<template>
  <UEmpty v-bind="{ ...$attrs, ...forwardedProps }" class="p-empty">
    <template v-if="$slots.leading || emptyIcon" #leading="{ ui }">
      <slot name="leading" :ui="ui">
        <UAvatar v-bind="avatar" data-slot="avatar" :class="ui.avatar({ class: props.ui?.avatar })">
          <FontAwesomeIcon
            v-if="emptyIcon"
            :icon="emptyIcon"
            aria-hidden="true"
            data-slot="icon"
            class="size-3/5"
            :class="{ 'animate-spin': loading }"
          />
        </UAvatar>
      </slot>
    </template>
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <template v-if="$slots.title" #title><slot name="title" /></template>
    <template v-if="$slots.description" #description><slot name="description" /></template>
    <template v-if="$slots.body" #body><slot name="body" /></template>
    <template v-if="$slots.actions || actions?.length" #actions>
      <slot name="actions">
        <PButton v-for="(action, index) in actions" :key="index" :size="size" v-bind="action" />
      </slot>
    </template>
    <template v-if="$slots.footer" #footer><slot name="footer" /></template>
  </UEmpty>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconDefinition, IconProp } from "@fortawesome/fontawesome-svg-core"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import UEmpty, { type EmptyProps, type EmptySlots } from "@nuxt/ui/components/Empty.vue"
import UAvatar from "@nuxt/ui/components/Avatar.vue"
import PButton from "@/components/elements/PButton.vue"

interface Props extends Omit<EmptyProps, "icon" | "loadingIcon" | "actions"> {
  /** FontAwesome icon displayed above the title. */
  icon?: [string, string] | IconDefinition
  /** FontAwesome icon displayed while loading. */
  loadingIcon?: [string, string] | IconDefinition
  /** Actions rendered with PButton, including FontAwesome icons. */
  actions?: Pick<
    InstanceType<typeof PButton>["$props"],
    "label" | "icon" | "color" | "variant" | "size" | "disabled" | "loading" | "to" | "onClick"
  >[]
}

const props = defineProps<Props>()
const forwardedProps = reactiveOmit(props, "icon", "loadingIcon", "actions")
const emptyIcon = computed(
  () => (props.loading ? props.loadingIcon || faSpinner : props.icon) as IconProp | undefined
)
defineOptions({ inheritAttrs: false })
defineSlots<EmptySlots>()
</script>
